import React, { useEffect, useMemo, useState } from "react";
import { FaCheck, FaInfoCircle, FaTimes } from "react-icons/fa";
import { FaInstagram } from "react-icons/fa6";
import { toast } from "react-toastify";
import { httpReauest } from "../../utils/httpRequest";

const IG_URL = "https://www.instagram.com/filetrust/";
const START_WINDOW_MS = 15 * 60 * 1000; // 15 minutes to complete the two-click flow

const InstagramTask = ({ user, reward, getData }) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);

  // a per-user key so multiple users don't collide
  const storageKey = useMemo(
    () => (user?._id ? `igStart:${user._id}` : `igStart:guest`),
    [user?._id]
  );

  // read current state from localStorage
  const readStartState = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return { count: 0, firstAt: 0 };
      const parsed = JSON.parse(raw);
      return {
        count: Number(parsed.count || 0),
        firstAt: Number(parsed.firstAt || 0),
      };
    } catch {
      return { count: 0, firstAt: 0 };
    }
  };

  const writeStartState = (count, firstAt) => {
    localStorage.setItem(storageKey, JSON.stringify({ count, firstAt }));
  };

  const resetStartState = () => writeStartState(0, 0);

  // expire the two-click window if it's too old
  useEffect(() => {
    const { count, firstAt } = readStartState();
    if (count > 0 && Date.now() - firstAt > START_WINDOW_MS) {
      resetStartState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const handleStartClick = () => {
    window.open(IG_URL, "_blank");

    const now = Date.now();
    const { count, firstAt } = readStartState();

    // If window expired, reset
    if (count > 0 && now - firstAt > START_WINDOW_MS) {
      resetStartState();
    }

    const { count: c2, firstAt: f2 } = readStartState();
    if (c2 === 0) {
      writeStartState(1, now);
    } else if (c2 === 1) {
      writeStartState(2, f2 || now);
      toast.success("Great! Now click Check to submit your username.");
    } else {
      // already 2 or more within window
      toast.info("You can now click Check to continue.");
    }
  };

  const handleCheckClick = () => {
    const { count, firstAt } = readStartState();
    const withinWindow = firstAt && Date.now() - firstAt <= START_WINDOW_MS;

    if (count < 2 || !withinWindow) {
      if (!withinWindow && count > 0) {
        resetStartState();
        toast.warn("Time window expired. Try Again and follow our instagram, it seems you did not follow it");
      } else {
        toast.warn(
          "Try Again and follow our instagram, it seems you did not follow it"
        );
      }
      return;
    }

    setModalOpen(true);
  };

  const handleSubmitUsername = async () => {
    if (!username) {
      toast.error("Please enter your Instagram username.");
      return;
    }

    setLoading(true);
    try {
      const userStorage = JSON.parse(localStorage.getItem("user"));
      await httpReauest(
        "POST",
        "/user/instagram/username",
        { username },
        { "x-access-token": userStorage?.token }
      );

      toast.success("Submitted successfully.");
      getData && getData();
      setModalOpen(false);
      setUsername("");
      resetStartState(); // reset the two-click flow after success
    } catch (err) {
      toast.error(err?.response?.data?.message || "Submission failed.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${
        (user?.instagram === "claimed" || user?.instagram === "progress") &&
        "order-last opacity-40"
      } flex justify-between gap-3 items-center relative border-b py-2 pb-4 text-white`}
    >
      <span className="flex items-center gap-2 lg:gap-4">
        <FaInstagram className="text-[32px] sm:text-[54px] lg:text-[72px]" />
        <span>
          <h4 className="font-bold text-[14px] lg:text-[28px] lg:-mb-2 flex items-center gap-2">
            Follow Instagram{" "}
            <FaInfoCircle
              className="text-blue-400 cursor-pointer"
              onClick={() => setIsHelpOpen(true)}
              title="Help"
            />
          </h4>
          <span className="font-bold text-[16px] lg:text-[28px] flex gap-1 items-center">
            <img src="/gold.png" className="w-6 lg:w-8" alt="GOLD" />+{reward}
          </span>
        </span>
      </span>

      {user?.instagram === "progress" ? (
        <button
          disabled
          className="start-button px-4 lg:px-8 py-2 bg-[#02006A] shadow shadow-white/40 rounded-full text-[14px] lg:text-[24px] flex gap-2 items-center"
        >
          In Progress...
        </button>
      ) : user?.instagram === "claimed" ? (
        <button
          disabled
          className="start-button px-4 lg:px-8 py-2 bg-[#02006A] shadow shadow-white/40 rounded-full text-[14px] lg:text-[24px] flex gap-2 items-center"
        >
          <FaCheck color="green" /> Claimed
        </button>
      ) : (
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            <button
              onClick={handleStartClick}
              className="start-button px-4 lg:px-8 py-2 bg-[#02006A] shadow shadow-white/40 rounded-full text-[14px] lg:text-[24px]"
            >
              Start
            </button>
            <button
              onClick={handleCheckClick}
              className="start-button px-4 lg:px-8 py-2 bg-[#02006A] shadow shadow-white/40 rounded-full text-[14px] lg:text-[24px]"
            >
              Check
            </button>
          </div>
        </div>
      )}

      {/* Modal: enter username after two Start clicks */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1e293b] p-6 rounded-xl w-full max-w-md text-white shadow-xl">
            <h3 className="text-xl font-semibold mb-4">
              Enter your Instagram username
            </h3>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="username"
              className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-white/10 text-white mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setModalOpen(false);
                  // Optional: reset flow if user cancels
                  resetStartState();
                }}
                className="px-4 py-2 rounded bg-gray-600 hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitUsername}
                disabled={loading}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700"
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {isHelpOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
          <div className="bg-[#1e293b] rounded-xl max-w-[90%] w-full sm:w-[400px] p-4">
            <div className="flex justify-between items-center mb-4 text-white">
              <h2 className="text-xl font-bold">How This Task Works</h2>
              <button
                onClick={() => setIsHelpOpen(false)}
                className="text-lg font-bold"
              >
                <FaTimes />
              </button>
            </div>
            <p className="text-gray-100 text-left">
              After you submit your username, it may take up to 24 hours for the
              system to verify whether you have followed our Instagram account.
              If your follow is confirmed, the reward will be automatically
              added to your account.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramTask;
